import React from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import {
  Card,
  ScrollArea,
  ListItemIcon,
  ListItemIconWrapper,
} from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomSkeleton from "components/common/CustomSkeleton";
import { Center, Stack } from "components/layout/reusableLayouts";

export type InsightSeverity = "critical" | "warning" | "info";

export interface ProgramInsight {
  id: string;
  severity: InsightSeverity;
  /** One translated sentence naming what happened. */
  title: string;
  /** The parts printed after the severity, already translated and formatted. */
  details: string[];
  icon: IconSvgElement;
  wrapper: ListItemIconWrapper;
  /** The page that can clear the insight. Without one the row is not a button. */
  route?: string;
}

interface ProgramInsightsPanelProps {
  insights: ProgramInsight[];
  loading?: boolean;
}

const SEVERITY_COLOR = {
  critical: "var(--color-text-danger)",
  warning: "var(--color-text-warning)",
} as const;

/** An info row carries no severity label: it is news, not a problem. */
const InsightContent: React.FC<{ insight: ProgramInsight }> = ({ insight }) => {
  const { t } = useTranslation();
  const details = insight.details.join(" · ");

  const severityLabel = {
    critical: t("Critical"),
    warning: t("Warning"),
  };

  return (
    <>
      <ListItemIcon sx={{ minWidth: 0 }} wrapper={insight.wrapper}>
        <HugeiconsIcon icon={insight.icon} size={20} />
      </ListItemIcon>
      <Stack gap="var(--space-1)" sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2">{insight.title}</Typography>
        <Typography
          variant="caption"
          sx={{ color: "var(--color-text-tertiary)" }}
        >
          {insight.severity === "info" ? (
            details
          ) : (
            <>
              <Typography
                component="span"
                variant="inherit"
                sx={{ color: SEVERITY_COLOR[insight.severity] }}
              >
                {severityLabel[insight.severity]}
              </Typography>
              {details && ` · ${details}`}
            </>
          )}
        </Typography>
      </Stack>
    </>
  );
};

/**
 * The dashboard's short list of things worth doing something about. The
 * ranking, the thresholds and where each row leads belong to the hook that
 * builds the insights; this only renders what it is handed.
 */
const ProgramInsightsPanel: React.FC<ProgramInsightsPanelProps> = ({
  insights,
  loading = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card
      variant="outlined"
      title={t("Program insights")}
      subtitle={t("Top things to act on")}
      sx={{ height: "100%" }}
    >
      {loading && insights.length === 0 ? (
        <CustomSkeleton variant="list" rows={4} />
      ) : insights.length === 0 ? (
        <Center sx={{ flex: 1, justifyContent: "center", textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{ color: "var(--color-text-tertiary)" }}
          >
            {t("Nothing needs your attention")}
          </Typography>
        </Center>
      ) : (
        <ScrollArea sx={{ flex: 1, minHeight: 0 }}>
          <List disablePadding>
            {insights.map((insight) => {
              const { route } = insight;
              return route ? (
                <ListItemButton
                  key={insight.id}
                  onClick={() => navigate(route)}
                >
                  <InsightContent insight={insight} />
                </ListItemButton>
              ) : (
                <ListItem key={insight.id}>
                  <InsightContent insight={insight} />
                </ListItem>
              );
            })}
          </List>
        </ScrollArea>
      )}
    </Card>
  );
};

export default ProgramInsightsPanel;
